"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Send, CircleCheck, CircleAlert, MapPin, Calendar, DollarSign, Compass } from "lucide-react";
const projectTypes = ["2009", "2010", "2011", "2012", "2013", "2014", "2015", "2016", "2017", "2018", "2019", "2020", "2021", "2022", "2023", "2024"];
const timelines = ["$0 - $5,000", "$5,000 - $15,000", "$15,000 - $30,000", "$30,000 - $60,000", "$60,000+"];
const budgets = ["Under $10,000", "$10,000 - $25,000", "$25,000 - $50,000", "$50,000 - $100,000", "$100,000+"];
function QualificationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-chart-2/10 rounded-md flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-chart-2" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Calculation Complete</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-primary text-primary hover:bg-primary/5">
          Recalculate Room
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="space-y-6 pt-4">
          <FormField control={control} name="projectType" rules={{
          required: "Please select your eligibility year"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="flex items-center gap-2 font-default text-foreground">
                  <Compass className="size-4 text-primary" />
                  Year You Turned 18
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background border-border rounded-md font-mono">
                      <SelectValue placeholder="Select the year you became eligible" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="font-mono">
                    {projectTypes.map((type, i) => <SelectItem key={i} value={type} data-index={i}>
                        {type}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormDescription className="text-muted-foreground">TFSA room only accumulates for years you were 18+ and a Canadian resident.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="timeline" rules={{
            required: "Please enter your total contributions"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default text-foreground">
                    <DollarSign className="size-4 text-primary" />
                    Total Past Contributions
                  </FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="e.g. 25000" {...field} className="bg-background border-border focus:ring-primary rounded-md font-mono" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="budget" rules={{
            required: "Please enter total withdrawals"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default text-foreground">
                    <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
                    Total Past Withdrawals
                  </FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="e.g. 5000" {...field} className="bg-background border-border focus:ring-primary rounded-md font-mono" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
            <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-destructive font-mono">{submitError}</p>
          </div>}

        <div className="pt-6">
          <Button type="submit" size="lg" disabled={isSubmitting} className="w-full relative overflow-hidden group bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-md rounded-md">
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
            <span className="relative flex items-center justify-center gap-2 font-default font-bold tracking-tight">
              {isSubmitting ? <>
                  <Loader className="animate-spin size-5" />
                  Calculating Room...
                </> : <>
                  Calculate My TFSA Room
                  <Send className="size-4 ml-1 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </>}
            </span>
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-4 flex items-center justify-center gap-1 font-mono">
            <MapPin className="size-3" />
            Based on CRA 2026 Contribution Limits
          </p>
        </div>
      </div>
    </Form>;
}
export default function LeadQualificationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="255457" id="contribution-logic" className="relative py-24 px-4 md:px-8 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-lg bg-secondary/5 blur-3xl" />
      </div>

      <div ref={ref} className={`max-w-3xl mx-auto relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-10 space-y-4">
          <h2 className="text-3xl md:text-5xl font-default font-extrabold text-foreground tracking-tighter">
            TFSA Contribution Room Planner
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Accurately track your available tax-free savings space based on Ontario residency and historical CRA limits.
          </p>
        </div>

        <Card className="shadow-xl border-border bg-card/95 backdrop-blur-sm rounded-md">
          <CardHeader className="pb-8 border-b border-border bg-muted/30 rounded-t-md">
            <CardTitle className="text-xl font-default font-bold text-center tracking-tight">Contribution Logic Engine</CardTitle>
            <CardDescription className="text-center font-default">
              Enter your details below to generate a real-time report of your available contribution room.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formKey="tfsa_contribution_form_tfsa_planner" formId="tfsa_contribution_form_tfsa_planner" sectionName="LeadQualificationForm" defaultValues={{
            projectType: "",
            timeline: "",
            budget: ""
          }} submitText="Calculate My TFSA Room" successMessage="Calculation complete! Your estimated TFSA contribution room has been generated. A detailed breakdown has been sent to your email.">
              <QualificationFormInner />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}